import { jsx as _jsx } from "react/jsx-runtime";
import { useLayoutEffect, useRef } from 'react';
/* ─── Live thousands-separated amount input ─────────────────────────────
 *
 * A native ``<input type="number">`` cannot show grouping separators — the
 * browser only allows a bare numeric token. So this is a text input whose
 * *display* is comma-grouped ("30,000.50") while the value handed back to
 * the parent stays a clean, parseable numeric string ("30000.5"). Typing
 * 30000 reformats to 30,000 on the fly, and the caret is kept in place by
 * counting the significant (digit / dot) characters to its left and
 * re-seating it after the same count in the regrouped string.
 * --------------------------------------------------------------------- */
/** Strip everything the display layer added (commas, stray chars), leaving a
 *  clean numeric string with at most one decimal point. No negatives — money
 *  amounts on a ledger line are always entered as positive magnitudes. */
export function sanitizeAmount(input) {
    let s = input.replace(/[^\d.]/g, '');
    const dot = s.indexOf('.');
    if (dot !== -1) {
        // collapse any further dots into the fractional part
        s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, '');
    }
    return s;
}
/** Format a clean numeric string with thousands separators, preserving a
 *  partial decimal the user is still typing ("30000." → "30,000."). */
export function formatAmount(raw) {
    if (raw === '')
        return '';
    const [intPart, ...rest] = raw.split('.');
    const grouped = (intPart ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (rest.length === 0 && !raw.includes('.'))
        return grouped;
    return `${grouped}.${rest.join('')}`;
}
/** How many significant (digit or dot) chars are in a slice — i.e. the chars
 *  that survive into the formatted string, used to anchor the caret. */
function significantCount(s) {
    return (s.match(/[\d.]/g) ?? []).length;
}
/** Map a significant-char count back to a string index in the formatted text. */
function caretFromSignificant(formatted, count) {
    if (count <= 0)
        return 0;
    let seen = 0;
    for (let i = 0; i < formatted.length; i++) {
        if (/[\d.]/.test(formatted[i])) {
            seen++;
            if (seen === count)
                return i + 1;
        }
    }
    return formatted.length;
}
export function AmountInput({ value, onChange, disabled, placeholder, className, ...rest }) {
    const ref = useRef(null);
    const pendingCaret = useRef(null);
    const display = formatAmount(value);
    // Re-seat the caret after a reformat so typing in the middle of a number
    // doesn't kick the cursor to the end.
    useLayoutEffect(() => {
        if (pendingCaret.current == null || !ref.current)
            return;
        const pos = caretFromSignificant(display, pendingCaret.current);
        ref.current.setSelectionRange(pos, pos);
        pendingCaret.current = null;
    }, [display]);
    function handleChange(e) {
        const el = e.target;
        const caret = el.selectionStart ?? el.value.length;
        pendingCaret.current = significantCount(el.value.slice(0, caret));
        onChange(sanitizeAmount(el.value));
    }
    return (_jsx("input", { ref: ref, type: "text", inputMode: "decimal", value: display, disabled: disabled, placeholder: placeholder, onChange: handleChange, className: className, ...rest }));
}
